"use client"

import React, { createContext, useContext } from "react"

export interface AppShellNavigationItem {
  key: string
  href: string
  base_href: string
  createHref?: string
  /** Accessible name for the create shortcut. Defaults to `New <title>`. */
  createLabel?: string
  icon?: React.ElementType
  title: string
  isFavorite?: boolean
  /**
   * Selects the row on its own page only, instead of on everything below it.
   * For an entry whose child pages have their own rows in the sidebar — Sofia
   * unfolds Investigations under itself — the prefix match would leave
   * both the parent and the child looking selected at once.
   */
  matchExact?: boolean
  /**
   * Intercepts activation of the row's link. For entries that are a live
   * surface rather than a page — the handler decides whether to take over and
   * `preventDefault`, or let `href` navigate as usual.
   */
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void
  /**
   * The global keyboard chord that reaches this destination, as the shortcut
   * registry stores it (`"g t"`). The row shows it on hover, so the way to the
   * page without the pointer is learned at the place the pointer already is.
   * Omitted for an entry with no chord of its own.
   */
  shortcutKey?: string
  subItems?: AppShellNavigationItem[]
}

export interface PrimaryNavItemContributionProps {
  item: AppShellNavigationItem
  getHref: (baseHref: string) => string
  isActive: (href: string, baseHref?: string, matchExact?: boolean) => boolean
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

export interface PrimaryNavItemChildrenContributionProps {
  item: AppShellNavigationItem
}

export interface SidebarFooterContributionProps {
  orgSlug?: string
  orgId?: string
}

export interface SidebarPrimaryBeforeNavContributionProps {
  className?: string
}

export interface DefaultPageHeaderContributionProps {
  getHref: (baseHref: string) => string
  isDashboardPage: boolean
}

export interface StatusBannerContributionProps {
  /** Layout classes from the call site, so the shell owns the placement. */
  className?: string
}

export interface HeaderTrailingContributionProps {
  /** Layout classes from the call site (inline header slot vs. absolute on header-less pages). */
  className?: string
}

export interface AppShellContributions {
  primaryNavigationItems?: AppShellNavigationItem[]
  renderPrimaryNavItem?: (
    props: PrimaryNavItemContributionProps,
  ) => React.ReactNode
  /**
   * Rows a module hangs under its own top-level nav entry — navigation it owns
   * rather than declares. Rendered directly below the entry's row, so a module
   * can unfold its own rows there while its section is the current one and fold
   * them away again when it is not.
   *
   * Consulted ONLY for an entry that declares no static `subItems`. A section
   * states its rows in config and this states them in code; an entry has one
   * source or the other, never both, so there is no question of which wins.
   * Adding this to an entry that already declares `subItems` renders nothing —
   * pinned by `SidebarNav.test.tsx`.
   */
  renderPrimaryNavItemChildren?: (
    props: PrimaryNavItemChildrenContributionProps,
  ) => React.ReactNode
  /**
   * Width class for the primary sidebar, overriding the default expanded
   * width. Lets a module that unfolds navigation into the sidebar give those
   * rows room, and hand the width back when it folds them away.
   */
  primaryNavWidthClass?: string
  renderOrganizationControl?: (props: {
    onNavigate?: () => void
  }) => React.ReactNode
  renderSidebarFooterBeforeOrg?: (
    props: SidebarFooterContributionProps,
  ) => React.ReactNode
  renderSidebarPrimaryBeforeNav?: (
    props: SidebarPrimaryBeforeNavContributionProps,
  ) => React.ReactNode
  renderDefaultPageHeader?: (
    props: DefaultPageHeaderContributionProps,
  ) => React.ReactNode
  /** Trailing element at the end of the page-header row (e.g. the Sofia panel toggle). */
  renderHeaderTrailing?: (
    props: HeaderTrailingContributionProps,
  ) => React.ReactNode
  /**
   * Notice shown above the page on every signed-in route — what a module needs
   * the customer to see wherever they are, rather than on the one page that
   * happens to own the query. Renders nothing when the module has nothing to
   * say, so the slot costs no space.
   */
  renderStatusBanner?: (props: StatusBannerContributionProps) => React.ReactNode
  renderCommandPalette?: () => React.ReactNode
}

const AppShellContributionsContext = createContext<AppShellContributions>({})

export const AppShellContributionsProvider = ({
  children,
  contributions,
}: {
  children: React.ReactNode
  contributions: AppShellContributions
}) => {
  return (
    <AppShellContributionsContext.Provider value={contributions}>
      {children}
    </AppShellContributionsContext.Provider>
  )
}

export const useAppShellContributions = () =>
  useContext(AppShellContributionsContext)
